import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { RootState } from '@umanforge/common';

import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { PlusIcon } from '@common_components/icons/plus.icon';

import BaseModal from '../base_modal.component';
import ConfirmationModal from '@common_components/modals/confirmationModal.component';
import { dayjs } from '@umanforge/common';
import { TaskAction, TaskActionPost, TaskActionPut } from 'interfaces/taskAction.interface';
import { TaskActionService } from 'services/taskAction.service';
import { ToastService } from 'services/toast.service';
import BottomModal from '@common_components/modals/bottomModal.component';
import InputImage from 'components/inputs/inputImage.component';
import InputText from '@common_components/inputs/textInput.component';

interface TaskActionModalProps {
  onTaskActionCreate?: ( taskAction: TaskActionPost ) => void | undefined;
  onUpdate?: ( taskAction: TaskActionPut ) => void | undefined;
  onDeleteTaskAction?: ( taskActionid: string ) => void | undefined;
  onClose: () => void;
  planningId: string;
  sectorId: string;
  tableNameProps?: string;
  taskActionId?: string;
  timeStartDrag?: dayjs.Dayjs;
  timeEndDrag?: dayjs.Dayjs;
  dateDrag?: dayjs.Dayjs;
}

interface TaskActionErrors {
  name: string | undefined;
  icon: string | undefined;
}

const TaskActionModal: React.FC<TaskActionModalProps> = ( {
  onTaskActionCreate,
  onDeleteTaskAction,
  onClose,
  onUpdate,
  taskActionId,
  sectorId
} ) => {
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [ errors, setErrors ] = useState<TaskActionErrors>( { name: undefined, icon: undefined } );

  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState( false );
  const [ isFormValid, setIsFormValid ] = useState( false );
  const [ isFormChanged, setIsFormChanged ] = useState( false );
  const [ isFirstRender, setIsFirstRender ] = useState( true );

  const [ taskAction, setTaskAction ] = useState<TaskAction>( );
  const [ name, setName ] = useState<string>();
  // Correction : icon peut être string (pour l'affichage) ou File (pour l'upload)
  const [ icon, setIcon ] = useState<string | File>();

  const handleCloseModal = () => {
    document.body.style.overflow = 'unset';
    onClose();
  };

  const fetchTaskAction = async () => {
    if ( !taskActionId ) {return;}

    try {
      const _taskAction = await TaskActionService.getTaskActionById( taskActionId, token, dispatch );
      setTaskAction( _taskAction );
      setName( _taskAction?.name );
      setIcon( _taskAction?.icon );

    }
    catch ( error ) {
      console.error( 'Error fetching taskAction:', error );
    }
  };

  useEffect( () => {
    if ( !isFirstRender && taskActionId ) {
      fetchTaskAction();
    }
  }, [ isFirstRender ] );

  useEffect( () => {
    setIsFirstRender( false );
  }, [ ] );

  useEffect( () => {
    const isValid = ( validateForm() );

    const isChanged = taskActionId ? (
      taskAction?.name != name ||
      taskAction?.icon != icon
    ) : (
      ( name && name.trim() !== '' ) ||
  ( icon && ( ( typeof icon === 'string' && icon.trim() !== '' ) || icon instanceof File ) )
    );

    setIsFormValid( isValid && Boolean( isChanged ) );
    setIsFormChanged( Boolean( isChanged ) );
  }, [ name, icon, taskAction, taskActionId ] );

  const validateForm = (): boolean => {
    const newErrors: TaskActionErrors = { name: undefined, icon: undefined };
    const regex = new RegExp( /^[^,\/]+$/ );

    if ( icon === '' || icon == null ) {
      return false;
    }
    if ( name === '' || name == null ) {
      return false;
    }

    else if ( name.length < 3 ) {
      newErrors.name = t( 'translation:task_action_name_too_short' );
    }
    else if ( name.length > 255 ) {
      newErrors.name = t( 'translation:task_action_name_too_long' );
    }
    else if ( !regex.test( name ) ) {
      newErrors.name = t( 'translation:task_action_name_contains_invalid_characters' );
    }


    setErrors( newErrors );
    return ( !newErrors.name && !newErrors.icon );
  };

  const handleSubmit = async () => {
    if ( !isFormValid ) {
      if ( !isFormChanged ) {
        ToastService.context( 'TaskActionModal' ).showError( 'translation:no_changes_to_save' );
        return;
      }
      else {
        ToastService.context( 'TaskActionModal' ).showError( 'translation:please_fill_required_fields' );
        return;
      }
    }

    // Block submission if icon is not a File (for new TaskAction)
    if ( !taskActionId && !( icon instanceof File ) ) {
      ToastService.context( 'TaskActionModal' ).showError( 'translation:please_select_an_icon_file' );
      return;
    }


    if ( onTaskActionCreate && !taskActionId ) {
      const base = {
        name: name!,
        icon: icon instanceof File ? icon : '', // force File ou string vide
        idSector: sectorId
      } as TaskActionPost;

      const newTaskId = await onTaskActionCreate( base );
      if ( newTaskId ) {
        handleCloseModal();
        return;
      }
    }

    if ( onUpdate && taskActionId ) {
      const base = {
        id: taskActionId ?? '',
        name: name!,
        icon: icon instanceof File ? icon : '' // force File ou string vide
      } as TaskActionPut;

      onUpdate( base );
    }

    handleCloseModal();
  };

  const handleDelete = () => {
    if ( taskActionId && onDeleteTaskAction ) {
      onDeleteTaskAction( taskActionId );
    }
    handleCloseModal();
  };


  return (
    <>
      <BaseModal
        isOpen={true}
        onClose={handleCloseModal}
        noConfirmationModal={!isFormChanged}
        icon={taskActionId ?
          <EditBoxIcon className="bg-blue-200 p-2 rounded-md" size={10} /> :
          <PlusIcon className="bg-green-200 p-2 rounded-md" size={10} />
        }
        title={taskActionId ? t( 'translation:edit_task_action' ) : t( 'translation:create_task_action' )}
        footer={
          <BottomModal
            tableId={taskActionId}
            formValid={isFormValid}
            onCreate={handleSubmit}
            onUpdate={handleSubmit}
            onDelete={() => {return setIsDeleteModalOpen( true );}}
          />
        }
      >
        <div className="py-5 px-7 gap-y-4 bg-white font-texte flex-col flex">
          <div className="flex flex-col space-y-2">
            <div className='flex'>
              <div className={ 'flex gap-2 relative items-center w-1/2' }>
                <label className='text-gray-600 truncate'>
                  { t( 'translation:task_action_name' ) }
                </label>
              </div>
              <InputText
                maxCharacter={ 255 }
                placeholder={ t( 'translation:task_action_name' ) }
                value={ name }
                onChange={ setName }
                type='text'
                className='w-full max-w-full'
              />
            </div>
            <div className="min-h-[1.25rem]">
              <p className="text-red-500 text-sm break-words whitespace-pre-wrap">{errors.name ?? ''}</p>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <InputImage
              label={ t( 'translation:task_action_icon' ) }
              value={ typeof icon === 'string' ? icon : undefined }
              onChange={ setIcon }
              textSize="w-5/12 mr-4"
            />
            <div className="min-h-[1.5rem]">
              <p className="text-red-500 text-sm break-words whitespace-pre-wrap">{ errors.icon ?? ''}</p>
            </div>
          </div>
        </div>
      </BaseModal>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleDelete}
        onCancel={() => {return setIsDeleteModalOpen( false );}}
        title={ t( 'translation:delete_task_action' ) }
        description={ t( 'translation:are_you_sure_that_you_want_to_delete_task_action_it_is_irreversible' ) }
      />
    </>
  );
};

export default TaskActionModal;
